import React, { useState, useEffect, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableFooter from '@mui/material/TableFooter';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import HandymanIcon from '@mui/icons-material/Handyman';
import CategoryIcon from '@mui/icons-material/Category';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getLookup, create, downloadExcel } from '../api/estimationApi';
import { getCached } from '../utils/apiCache';
import * as materialApi from '../api/materialApi';
import * as accessoryApi from '../api/accessoryApi';
import * as priceApi from '../api/priceApi';
import * as accessoryPriceApi from '../api/accessoryPriceApi';
import { useSnackbar } from '../context/SnackbarContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';

const fmt = (v) => `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

const QTY_NOS = ['1','2','3','4','5','6','7','8','9','10','12','15','20','25','30','50','100'];
const QTY_MTR = ['1','5','10','15','20','25','50','100','150','200','500'];

const EMPTY_ITEM = {
  departmentId: '', materialGroupId: '',
  materialObj: null, materialId: '', unit: '',
  makeObj: null, makeId: '', price: '',
  quantity: '', matSearch: '', matOptions: [], priceOptions: [],
};

const EMPTY_ACC_ITEM = {
  departmentId: '', accessoryGroupId: '',
  accessoryObj: null, accessoryId: '', unit: '',
  makeObj: null, makeId: '', price: '',
  quantity: '', accSearch: '', accOptions: [], priceOptions: [],
};

const isItemValid = (item) => {
  if (!item.materialId || !item.makeId) return false;
  const qty = Number(item.quantity);
  if (!item.quantity || isNaN(qty) || qty <= 0) return false;
  if (item.unit === 'NOS' && !Number.isInteger(qty)) return false;
  return true;
};

const isAccItemValid = (item) => {
  if (!item.accessoryId || !item.makeId) return false;
  const qty = Number(item.quantity);
  if (!item.quantity || isNaN(qty) || qty <= 0) return false;
  if (item.unit === 'NOS' && !Number.isInteger(qty)) return false;
  return true;
};

// Returns true if the acc item is "in progress" (partially filled) but not valid
const isAccItemPartial = (item) => {
  return !isAccItemValid(item) && (item.accessoryId || item.makeId || item.quantity);
};

export default function Estimation() {
  const [lookup, setLookup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [accItems, setAccItems] = useState([]);
  const [showAccessories, setShowAccessories] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedId, setLastSavedId] = useState(null);
  const { showSnackbar } = useSnackbar();
  const searchTimers = useRef({});
  const accSearchTimers = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Cache for 5 min — avoids re-loading all materials/prices on every estimation start
      setLookup(await getCached('estimation:lookup', () => getLookup()));
    }
    catch (e) { showSnackbar(e.message, 'error'); }
    finally { setLoading(false); }
  }, [showSnackbar]);

  useEffect(() => { load(); }, [load]);

  // ── Material search ──────────────────────────────────────────────────────────
  const searchMaterials = (index, searchText, item) => {
    clearTimeout(searchTimers.current[index]);
    searchTimers.current[index] = setTimeout(async () => {
      try {
        const params = { limit: 50 };
        if (searchText) params.search = searchText;
        if (item.materialGroupId) params.materialGroupId = item.materialGroupId;
        else if (item.departmentId) params.departmentId = item.departmentId;
        const results = await materialApi.getAll(params);
        setItems((prev) => {
          const updated = [...prev];
          if (updated[index]) updated[index] = { ...updated[index], matOptions: results.data || results };
          return updated;
        });
      } catch (e) { console.error(e); }
    }, 300);
  };

  // ── Accessory search ─────────────────────────────────────────────────────────
  const searchAccessories = (index, searchText, item) => {
    clearTimeout(accSearchTimers.current[index]);
    accSearchTimers.current[index] = setTimeout(async () => {
      try {
        const params = { limit: 50 };
        if (searchText) params.search = searchText;
        if (item.accessoryGroupId) params.accessoryGroupId = item.accessoryGroupId;
        else if (item.departmentId) params.departmentId = item.departmentId;
        const results = await accessoryApi.getAll(params);
        setAccItems((prev) => {
          const updated = [...prev];
          if (updated[index]) updated[index] = { ...updated[index], accOptions: results.data || results };
          return updated;
        });
      } catch (e) { console.error(e); }
    }, 300);
  };

  // ── Per-selection price lookup ───────────────────────────────────────────────
  // Replaces the old "scan the whole preloaded lookup.prices array" approach —
  // fetches just the priced makes for ONE material/accessory (small, indexed
  // query) the moment it's selected, instead of preloading every price row
  // in the table up front.
  const loadMaterialPrices = (index, materialId) => {
    priceApi.getAll({ materialId })
      .then((res) => {
        const rows = res.data || res;
        const priceOptions = rows.map((p) => ({ id: p.makeId, name: p.make?.name, price: p.price }));
        setItems((prev) => {
          const updated = [...prev];
          if (updated[index]) updated[index] = { ...updated[index], priceOptions };
          return updated;
        });
      })
      .catch((e) => console.error(e));
  };

  const loadAccessoryPrices = (index, accessoryId) => {
    accessoryPriceApi.getAll({ accessoryId })
      .then((res) => {
        const rows = res.data || res;
        const priceOptions = rows.map((p) => ({ id: p.makeId, name: p.make?.name, price: p.price }));
        setAccItems((prev) => {
          const updated = [...prev];
          if (updated[index]) updated[index] = { ...updated[index], priceOptions };
          return updated;
        });
      })
      .catch((e) => console.error(e));
  };

  // ── Material item update ─────────────────────────────────────────────────────
  const updateItem = (index, field, value, extra = {}) => {
    setItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value, ...extra };

      if (field === 'departmentId') {
        item.materialGroupId = '';
        item.materialObj = null; item.materialId = '';
        item.unit = ''; item.makeObj = null; item.makeId = ''; item.price = '';
        item.quantity = ''; item.matOptions = []; item.priceOptions = [];
      }
      if (field === 'materialGroupId') {
        if (extra._groupDeptId) item.departmentId = extra._groupDeptId;
        item.materialObj = null; item.materialId = '';
        item.unit = ''; item.makeObj = null; item.makeId = ''; item.price = '';
        item.quantity = ''; item.priceOptions = [];
        searchMaterials(index, '', { ...item, materialGroupId: value, departmentId: item.departmentId });
      }
      if (field === 'materialObj') {
        const mat = value;
        item.materialId = mat ? mat.id : '';
        item.unit = mat ? mat.unit : '';
        item.makeObj = null; item.makeId = ''; item.price = ''; item.quantity = ''; item.priceOptions = [];
        if (mat) {
          if (mat.materialGroup?.department?.id) item.departmentId = mat.materialGroup.department.id;
          if (mat.materialGroup?.id) item.materialGroupId = mat.materialGroup.id;
          loadMaterialPrices(index, mat.id);
        }
      }
      if (field === 'makeId') {
        item.price = '';
        if (value) {
          const po = (item.priceOptions || []).find((p) => p.id === Number(value));
          item.price = po ? po.price : '';
        }
      }

      updated[index] = item;
      return updated;
    });
  };

  // ── Accessory item update ────────────────────────────────────────────────────
  const updateAccItem = (index, field, value, extra = {}) => {
    setAccItems((prev) => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value, ...extra };

      if (field === 'departmentId') {
        item.accessoryGroupId = '';
        item.accessoryObj = null; item.accessoryId = '';
        item.unit = ''; item.makeObj = null; item.makeId = ''; item.price = '';
        item.quantity = ''; item.accOptions = []; item.priceOptions = [];
      }
      if (field === 'accessoryGroupId') {
        if (extra._groupDeptId) item.departmentId = extra._groupDeptId;
        item.accessoryObj = null; item.accessoryId = '';
        item.unit = ''; item.makeObj = null; item.makeId = ''; item.price = '';
        item.quantity = ''; item.priceOptions = [];
        searchAccessories(index, '', { ...item, accessoryGroupId: value, departmentId: item.departmentId });
      }
      if (field === 'accessoryObj') {
        const acc = value;
        item.accessoryId = acc ? acc.id : '';
        item.unit = acc ? acc.unit : '';
        item.makeObj = null; item.makeId = ''; item.price = ''; item.quantity = ''; item.priceOptions = [];
        if (acc) {
          if (acc.accessoryGroup?.department?.id) item.departmentId = acc.accessoryGroup.department.id;
          if (acc.accessoryGroup?.id) item.accessoryGroupId = acc.accessoryGroup.id;
          loadAccessoryPrices(index, acc.id);
        }
      }
      if (field === 'makeId') {
        item.price = '';
        if (value) {
          const po = (item.priceOptions || []).find((p) => p.id === Number(value));
          item.price = po ? po.price : '';
        }
      }

      updated[index] = item;
      return updated;
    });
  };

  const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (index) => setItems((prev) => prev.filter((_, i) => i !== index));
  const addAccItem = () => setAccItems((prev) => [...prev, { ...EMPTY_ACC_ITEM }]);
  const removeAccItem = (index) => setAccItems((prev) => prev.filter((_, i) => i !== index));

  const getGroupOptions = (departmentId) => {
    if (!lookup) return [];
    if (!departmentId) return lookup.materialGroups;
    return lookup.materialGroups.filter((g) => g.departmentId === Number(departmentId));
  };

  const getAccGroupOptions = (departmentId) => {
    if (!lookup) return [];
    if (!departmentId) return lookup.accessoryGroups || [];
    return (lookup.accessoryGroups || []).filter((g) => g.departmentId === Number(departmentId));
  };

  // Makes available for a selection now come straight from that item's own
  // `priceOptions` (fetched per-material/per-accessory in loadMaterialPrices /
  // loadAccessoryPrices above) rather than filtering a whole preloaded price
  // table — same {id, name} shape the Make Autocomplete expects.
  const getFilteredMakes = (item) => item.priceOptions || [];
  const getAccFilteredMakes = (item) => item.priceOptions || [];

  const materialTotal = items.reduce((sum, item) =>
    sum + (item.price !== '' && item.quantity ? Number(item.price) * Number(item.quantity) : 0), 0);

  const accTotal = accItems.reduce((sum, item) =>
    sum + (item.price !== '' && item.quantity ? Number(item.price) * Number(item.quantity) : 0), 0);

  const grandTotal = materialTotal + accTotal;

  const hasPartialAccItems = accItems.some(isAccItemPartial);

  const isValid = () => {
    if (!title.trim() || items.length === 0) return false;
    if (!items.every(isItemValid)) return false;
    if (accItems.length > 0 && !accItems.every(isAccItemValid)) return false;
    return true;
  };

  const handleSubmit = async () => {
    setSaving(true);
    setLastSavedId(null);
    try {
      const payload = {
        title: title.trim(),
        lineItems: items.map((item) => ({
          materialId: Number(item.materialId),
          makeId: Number(item.makeId),
          quantity: Number(item.quantity),
        })),
        accessoryItems: accItems.map((item) => ({
          accessoryId: Number(item.accessoryId),
          makeId: Number(item.makeId),
          quantity: Number(item.quantity),
        })),
      };
      const result = await create(payload);
      setLastSavedId(result.id);
      showSnackbar(
        `Estimation "${result.title || title}" saved! Grand Total: ${fmt(result.grandTotal ?? grandTotal)}`,
        'success'
      );
      setTitle('');
      setItems([{ ...EMPTY_ITEM }]);
      setAccItems([]);
      setShowAccessories(false);
    } catch (e) {
      showSnackbar(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadExcel = async (id) => {
    try {
      await downloadExcel(id);
      showSnackbar('Estimation downloaded as Excel', 'success');
    } catch (e) {
      showSnackbar(e.message || 'Download failed', 'error');
    }
  };

  if (loading) return <LoadingSpinner message="Loading estimation data..." />;

  // ── Renders a single row for the table ────────────────────────────────────────
  const renderMatRow = (item, idx) => {
    const lineTotal = item.price !== '' && item.quantity ? Number(item.price) * Number(item.quantity) : null;
    const groupOptions = getGroupOptions(item.departmentId);
    const filteredMakes = getFilteredMakes(item);
    const selectedDeptObj = lookup?.departments.find((d) => d.id === Number(item.departmentId)) || null;
    const selectedGroupObj = lookup?.materialGroups.find((g) => g.id === Number(item.materialGroupId)) || null;
    const selectedMakeObj = filteredMakes.find((m) => m.id === Number(item.makeId)) || null;
    const isNos = item.unit === 'NOS';
    const qtyOptions = isNos ? QTY_NOS : QTY_MTR;
    const qty = Number(item.quantity);
    const qtyError = item.unit === 'NOS' && item.quantity !== '' && !isNaN(qty) && qty > 0 && !Number.isInteger(qty);

    return (
      <TableRow key={idx} sx={{ bgcolor: idx % 2 === 0 ? '#FAFAFA' : '#fff', verticalAlign: 'top' }}>
        <TableCell sx={{ pt: 1.5 }}><Chip label={idx + 1} size="small" /></TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 130 }}
            options={lookup?.departments || []} getOptionLabel={(o) => o.name || ''}
            value={selectedDeptObj} onChange={(_, v) => updateItem(idx, 'departmentId', v ? v.id : '')}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Department" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
          />
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 140 }}
            options={groupOptions} getOptionLabel={(o) => o.groupCode || ''}
            value={selectedGroupObj}
            onChange={(_, v) => updateItem(idx, 'materialGroupId', v ? v.id : '', { _groupDeptId: v ? v.departmentId : null })}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Group Code" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{o.groupCode}</Typography>
                  {!item.departmentId && <Typography variant="caption" color="text.secondary">{lookup?.departments.find((d) => d.id === o.departmentId)?.name || ''}</Typography>}
                </Box>
              </li>
            )}
          />
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 190 }}
            options={item.matOptions} getOptionLabel={(o) => o.name || ''}
            value={item.materialObj} onChange={(_, v) => updateItem(idx, 'materialObj', v)}
            onInputChange={(_, v) => { updateItem(idx, 'matSearch', v); searchMaterials(idx, v, item); }}
            filterOptions={(x) => x} isOptionEqualToValue={(o, v) => o.id === v?.id}
            onOpen={() => searchMaterials(idx, item.matSearch, item)}
            renderInput={(params) => <TextField {...params} label="Material" placeholder="Type to search…" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.materialGroup?.groupCode} · {o.materialGroup?.department?.name}</Typography>
                </Box>
              </li>
            )}
          />
        </TableCell>
        <TableCell sx={{ pt: 1.5 }}>
          {item.unit ? <Chip label={item.unit} size="small" color={isNos ? 'secondary' : 'primary'} variant="outlined" /> : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 130 }}
            options={filteredMakes} getOptionLabel={(o) => o.name || ''}
            value={selectedMakeObj} onChange={(_, v) => updateItem(idx, 'makeId', v ? v.id : '')}
            disabled={!item.materialId} isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Make" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
          />
        </TableCell>
        <TableCell sx={{ pt: 1.5 }}>
          {item.price !== '' ? <Typography fontWeight={600} color="success.dark" fontSize={13}>{fmt(item.price)}</Typography> : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell>
          <Autocomplete freeSolo size="small" sx={{ width: 135 }}
            options={item.unit ? qtyOptions : []} value={item.quantity || ''}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') updateItem(idx, 'quantity', v); }}
            onChange={(_, v) => { if (v !== null && v !== undefined) updateItem(idx, 'quantity', String(v)); }}
            disabled={!item.makeId}
            renderInput={(params) => (
              <TextField {...params}
                label={item.unit ? (isNos ? 'Qty (whole no.)' : `Qty (${item.unit})`) : 'Qty'}
                placeholder={isNos ? 'e.g. 5' : 'e.g. 2.5'}
                error={qtyError} helperText={qtyError ? 'Whole number required for NOS' : ''}
                inputProps={{ ...params.inputProps, inputMode: isNos ? 'numeric' : 'decimal' }}
              />
            )}
          />
        </TableCell>
        <TableCell align="right" sx={{ pt: 1.5, bgcolor: lineTotal !== null ? '#E3F2FD' : 'transparent' }}>
          {lineTotal !== null ? (
            <Box>
              <Typography fontWeight={700} color="primary.dark" fontSize={14}>{fmt(lineTotal)}</Typography>
              {item.quantity && item.price !== '' && <Typography variant="caption" color="text.secondary" display="block">{item.quantity} × {fmt(item.price)}</Typography>}
            </Box>
          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell sx={{ pt: 1 }}>
          <Tooltip title="Remove row"><span>
            <IconButton size="small" color="error" onClick={() => removeItem(idx)} disabled={items.length === 1}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span></Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const renderAccRow = (item, idx) => {
    const lineTotal = item.price !== '' && item.quantity ? Number(item.price) * Number(item.quantity) : null;
    const groupOptions = getAccGroupOptions(item.departmentId);
    const filteredMakes = getAccFilteredMakes(item);
    const selectedDeptObj = lookup?.departments.find((d) => d.id === Number(item.departmentId)) || null;
    const selectedGroupObj = (lookup?.accessoryGroups || []).find((g) => g.id === Number(item.accessoryGroupId)) || null;
    const selectedMakeObj = filteredMakes.find((m) => m.id === Number(item.makeId)) || null;
    const isNos = item.unit === 'NOS';
    const qtyOptions = isNos ? QTY_NOS : QTY_MTR;
    const qty = Number(item.quantity);
    const qtyError = item.unit === 'NOS' && item.quantity !== '' && !isNaN(qty) && qty > 0 && !Number.isInteger(qty);

    return (
      <TableRow key={idx} sx={{ bgcolor: idx % 2 === 0 ? '#FDF3FF' : '#FFF', verticalAlign: 'top' }}>
        <TableCell sx={{ pt: 1.5 }}><Chip label={idx + 1} size="small" color="secondary" /></TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 130 }}
            options={lookup?.departments || []} getOptionLabel={(o) => o.name || ''}
            value={selectedDeptObj} onChange={(_, v) => updateAccItem(idx, 'departmentId', v ? v.id : '')}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Department" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
          />
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 140 }}
            options={groupOptions} getOptionLabel={(o) => o.groupCode || ''}
            value={selectedGroupObj}
            onChange={(_, v) => updateAccItem(idx, 'accessoryGroupId', v ? v.id : '', { _groupDeptId: v ? v.departmentId : null })}
            isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Acc. Group" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{o.groupCode}</Typography>
                  {!item.departmentId && <Typography variant="caption" color="text.secondary">{lookup?.departments.find((d) => d.id === o.departmentId)?.name || ''}</Typography>}
                </Box>
              </li>
            )}
          />
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 190 }}
            options={item.accOptions} getOptionLabel={(o) => o.name || ''}
            value={item.accessoryObj} onChange={(_, v) => updateAccItem(idx, 'accessoryObj', v)}
            onInputChange={(_, v) => { updateAccItem(idx, 'accSearch', v); searchAccessories(idx, v, item); }}
            filterOptions={(x) => x} isOptionEqualToValue={(o, v) => o.id === v?.id}
            onOpen={() => searchAccessories(idx, item.accSearch, item)}
            renderInput={(params) => <TextField {...params} label="Accessory" placeholder="Type to search…" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
            renderOption={(props, o) => (
              <li {...props} key={o.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{o.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{o.accessoryGroup?.groupCode} · {o.accessoryGroup?.department?.name}</Typography>
                </Box>
              </li>
            )}
          />
        </TableCell>
        <TableCell sx={{ pt: 1.5 }}>
          {item.unit ? <Chip label={item.unit} size="small" variant="outlined" /> : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell>
          <Autocomplete size="small" sx={{ minWidth: 130 }}
            options={filteredMakes} getOptionLabel={(o) => o.name || ''}
            value={selectedMakeObj} onChange={(_, v) => updateAccItem(idx, 'makeId', v ? v.id : '')}
            disabled={!item.accessoryId} isOptionEqualToValue={(o, v) => o.id === v?.id}
            renderInput={(params) => <TextField {...params} label="Make" inputProps={{ ...params.inputProps, autoComplete: 'off' }} />}
          />
        </TableCell>
        <TableCell sx={{ pt: 1.5 }}>
          {item.price !== '' ? <Typography fontWeight={600} color="secondary.dark" fontSize={13}>{fmt(item.price)}</Typography> : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell>
          <Autocomplete freeSolo size="small" sx={{ width: 135 }}
            options={item.unit ? qtyOptions : []} value={item.quantity || ''}
            onInputChange={(_, v, reason) => { if (reason !== 'reset') updateAccItem(idx, 'quantity', v); }}
            onChange={(_, v) => { if (v !== null && v !== undefined) updateAccItem(idx, 'quantity', String(v)); }}
            disabled={!item.makeId}
            renderInput={(params) => (
              <TextField {...params}
                label={item.unit ? (isNos ? 'Qty (whole no.)' : `Qty (${item.unit})`) : 'Qty'}
                placeholder={isNos ? 'e.g. 5' : 'e.g. 2.5'}
                error={qtyError} helperText={qtyError ? 'Whole number required for NOS' : ''}
                inputProps={{ ...params.inputProps, inputMode: isNos ? 'numeric' : 'decimal' }}
              />
            )}
          />
        </TableCell>
        <TableCell align="right" sx={{ pt: 1.5, bgcolor: lineTotal !== null ? '#F3E5FF' : 'transparent' }}>
          {lineTotal !== null ? (
            <Box>
              <Typography fontWeight={700} color="secondary.dark" fontSize={14}>{fmt(lineTotal)}</Typography>
              {item.quantity && item.price !== '' && <Typography variant="caption" color="text.secondary" display="block">{item.quantity} × {fmt(item.price)}</Typography>}
            </Box>
          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell sx={{ pt: 1 }}>
          <Tooltip title="Remove row">
            <IconButton size="small" color="error" onClick={() => removeAccItem(idx)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  const tableHeaders = [
    { label: '#', width: 36 },
    { label: 'Department', minWidth: 140 },
    { label: 'Group Code', minWidth: 150 },
    { label: 'Item', minWidth: 200 },
    { label: 'Unit', width: 70, icon: true },
    { label: 'Make', minWidth: 140 },
    { label: 'Unit Price', width: 120, icon: true },
    { label: 'Quantity', width: 145 },
    { label: 'Line Total', width: 140, align: 'right' },
    { label: '', width: 44 },
  ];

  return (
    <Box>
      <PageHeader
        title="New Estimation"
        subtitle="Add material and accessory line items. Prices are auto-fetched from the current price list."
      />

      {lastSavedId && (
        <Alert severity="success" sx={{ mb: 2 }}
          action={<Button size="small" color="inherit" startIcon={<DownloadIcon />} onClick={() => handleDownloadExcel(lastSavedId)}>Download Excel</Button>}
        >
          Estimation saved! Download it as an Excel sheet.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField label="Estimation Title" fullWidth value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Project Alpha — Phase 1 BOM"
          />
        </CardContent>
      </Card>

      {/* ── Materials Section ───────────────────────────────────────────────────── */}
      <Card sx={{ mb: 2 }}>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#E3F2FD' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CategoryIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={700} color="primary.dark">Materials</Typography>
            <Chip label={`${items.length} row${items.length !== 1 ? 's' : ''}`} size="small" />
            {materialTotal > 0 && <Chip label={fmt(materialTotal)} size="small" color="primary" />}
          </Box>
          <Button variant="outlined" startIcon={<AddCircleIcon />} onClick={addItem} size="small">
            Add Row
          </Button>
        </Box>
        <Divider />
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table size="small" sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                {tableHeaders.map((h, i) => (
                  <TableCell key={i} sx={{ width: h.width, minWidth: h.minWidth }} align={h.align}>
                    {h.label}
                    {h.icon && <LockIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#bbb', ml: 0.5 }} />}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>{items.map(renderMatRow)}</TableBody>
            <TableFooter>
              <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                <TableCell colSpan={8} align="right" sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={700}>Materials Total</Typography>
                </TableCell>
                <TableCell align="right" sx={{ py: 1 }}>
                  <Typography variant="body2" fontWeight={700} color="primary">{fmt(materialTotal)}</Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableFooter>
          </Table>
        </Box>
      </Card>

      {/* ── Accessories Section (collapsible) ───────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#F3E5FF', cursor: 'pointer' }}
          onClick={() => setShowAccessories((v) => !v)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HandymanIcon sx={{ color: '#7B1FA2' }} />
            <Typography variant="subtitle1" fontWeight={700} color="secondary.dark">
              Accessories <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
            </Typography>
            {accItems.length > 0 && <Chip label={`${accItems.length} row${accItems.length !== 1 ? 's' : ''}`} size="small" color="secondary" />}
            {accTotal > 0 && <Chip label={fmt(accTotal)} size="small" sx={{ bgcolor: '#7B1FA2', color: '#fff' }} />}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {showAccessories && (
              <Button variant="outlined" size="small" startIcon={<AddCircleIcon />} color="secondary"
                onClick={(e) => { e.stopPropagation(); addAccItem(); }}
              >
                Add Row
              </Button>
            )}
            {showAccessories ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>
        <Collapse in={showAccessories}>
          <Divider />
          {accItems.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: '#9e9e9e' }}>
              <Typography variant="body2">No accessory items added yet.</Typography>
              <Button size="small" startIcon={<AddCircleIcon />} color="secondary" onClick={addAccItem} sx={{ mt: 1 }}>
                Add First Accessory Row
              </Button>
            </Box>
          ) : (
            <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table size="small" sx={{ minWidth: 1100 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#F9F0FF' }}>
                    {tableHeaders.map((h, i) => (
                      <TableCell key={i} sx={{ width: h.width, minWidth: h.minWidth }} align={h.align}>
                        {h.label}
                        {h.icon && <LockIcon sx={{ fontSize: 11, verticalAlign: 'middle', color: '#bbb', ml: 0.5 }} />}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>{accItems.map(renderAccRow)}</TableBody>
                <TableFooter>
                  <TableRow sx={{ bgcolor: '#F3E5FF' }}>
                    <TableCell colSpan={8} align="right" sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="secondary.dark">Accessories Total</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography variant="body2" fontWeight={700} color="secondary.dark">{fmt(accTotal)}</Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableFooter>
              </Table>
            </Box>
          )}
          {hasPartialAccItems && (
            <Alert severity="warning" sx={{ m: 2 }}>
              Some accessory rows are incomplete. Complete or remove them before saving.
            </Alert>
          )}
        </Collapse>
      </Card>

      {/* ── Grand Total & Save ───────────────────────────────────────────────────── */}
      <Card>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Materials</Typography>
              <Typography variant="h6" fontWeight={700} color="primary">{fmt(materialTotal)}</Typography>
            </Box>
            {accItems.length > 0 && (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary">Accessories</Typography>
                  <Typography variant="h6" fontWeight={700} color="secondary.dark">{fmt(accTotal)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Grand Total</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.dark">{fmt(grandTotal)}</Typography>
                </Box>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {!isValid() && title && (
              <Typography variant="caption" color="text.secondary">
                {hasPartialAccItems ? 'Complete or remove incomplete accessory rows' : 'Complete all fields to enable save'}
              </Typography>
            )}
            <Button variant="contained" size="large" startIcon={<SaveIcon />}
              onClick={handleSubmit} disabled={!isValid() || saving}
            >
              {saving ? 'Saving…' : 'Save Estimation'}
            </Button>
          </Box>
        </Box>
      </Card>
    </Box>
  );
}
